// Deal-domain client operations that go beyond the generic entity accessors:
// audit-trailed writes, deal point position changes, version publishing.
// Reads use useEntityList/useEntityDoc from data/entities.js directly.
import { db } from '../firebaseConfig';
import { createEntity, updateEntity } from './entities';
import { getAssetDownloadUrl } from './uploadService';
import { serverPublishVersion } from './legalApi';

// Large versions keep their clause arrays on S3 (Firestore's 1 MiB doc cap);
// the version doc then carries clausesS3Key instead of the inline array.
// Every clause read in the app should go through this helper.
export async function loadVersionClauses(version) {
  if (version?.clausesS3Key) {
    const url = await getAssetDownloadUrl({ key: version.clausesS3Key });
    const res = await fetch(url);
    if (!res.ok) throw new Error('failed to load document content');
    return res.json();
  }
  return version?.clauses || [];
}

export const PARTY_COLORS = [
  'var(--brand)', 'var(--info)', 'var(--warn)', 'var(--ok)', 'var(--danger)',
];

// Display labels for docGraph extractFacts types — used by the DocView facts
// panel and the dashboard's Key facts audit card.
export const FACT_TYPE_LABEL = {
  entity: 'Entities', jurisdiction: 'Jurisdictions', date: 'Dates',
  amount: 'Amounts', percent: 'Percentages', address: 'Addresses',
};

// Project taxonomy — every working concept is a PROJECT; a deal is one type.
// Negotiated types get parties + the Deal Points Register; authored types are
// document-first. Keys mirror PROJECT_TYPES in the backend entity registry.
export const PROJECT_TYPES = [
  { key: 'deal', label: 'Deal / negotiation', negotiated: true, icon: '🤝', hint: 'M&A, MSA, credit — a multi-party agreement negotiation' },
  { key: 'corporate', label: 'Corporate structure', negotiated: true, icon: '🏛️', hint: 'Formation & governance sets — operating agreements, HoldCo/OpCo, IP assignment' },
  { key: 'financing', label: 'Financing round', negotiated: true, icon: '💵', hint: 'A raise — term sheets, subscription and side agreements' },
  { key: 'litigation', label: 'Dispute / litigation', negotiated: true, icon: '⚖️', hint: 'Pleadings, discovery, settlement drafts' },
  { key: 'fdd', label: 'Functional design doc', negotiated: false, icon: '📐', hint: 'Software/hardware specification with structured sections' },
  { key: 'presentation', label: 'Presentation', negotiated: false, icon: '📽️', hint: 'A slide deck — agent-authored or imported' },
  { key: 'pitchbook', label: 'Pitchbook', negotiated: false, icon: '📈', hint: 'Investor or client pitch material' },
  { key: 'technical', label: 'Technical update', negotiated: false, icon: '🛠️', hint: 'Release notes, engineering status, architecture notes' },
  { key: 'memo', label: 'Memo', negotiated: false, icon: '📝', hint: 'Internal or client memorandum' },
  { key: 'book', label: 'Book / manuscript', negotiated: false, icon: '📚', hint: 'Long-form writing with chapters' },
  { key: 'journal', label: 'Journal / log', negotiated: false, icon: '🗓️', hint: 'Running log, lab notebook, meeting journal' },
  { key: 'research', label: 'Research / whitepaper', negotiated: false, icon: '🔬', hint: 'Research report, analysis, whitepaper' },
  { key: 'policy', label: 'Policy / compliance', negotiated: false, icon: '📋', hint: 'Policies, procedures, compliance documentation' },
  { key: 'playbook', label: 'Playbook / SOP', negotiated: false, icon: '🧭', hint: 'Operating procedures and playbooks' },
  { key: 'proposal', label: 'Proposal / RFP', negotiated: false, icon: '📬', hint: 'Proposals and RFP responses' },
  { key: 'grant', label: 'Grant application', negotiated: false, icon: '🏛️', hint: 'SBIR/STTR, state and local grants, incentives — one application per project' },
  { key: 'filing', label: 'Regulatory filing', negotiated: false, icon: '🏦', hint: 'Filings and submissions to regulators' },
  { key: 'ip', label: 'IP / USPTO filing', negotiated: false, icon: '®', hint: 'Trademark, patent & copyright prosecution — applications, office actions, renewals' },
];

export const projectTypeOf = (deal) =>
  PROJECT_TYPES.find((t) => t.key === (deal?.projectType || 'deal')) || PROJECT_TYPES[0];
// Legacy projects predate projectType — they are all deals (negotiated).
export const isNegotiated = (deal) => projectTypeOf(deal).negotiated;

// Lineage: a doc replaced by a newer document is history, not a peer — the
// Documents tab folds it away and dashboards skip it. Either the explicit
// pointer (supersededBy) or the provenance tag marks it.
export const isSupersededDoc = (d) => !!(d?.supersededBy || (d?.tags || []).includes('superseded'));

// Non-negotiated projects reuse the same status enum with friendlier labels.
export const STATUS_LABELS_AUTHORED = {
  draft: 'draft',
  negotiating: 'in progress',
  agreed: 'in review',
  executed: 'final',
  closed: 'archived',
};
export const statusLabel = (deal, status = deal?.status) =>
  isNegotiated(deal) ? status : (STATUS_LABELS_AUTHORED[status] || status);

export const DEFAULT_PARTIES = [
  { key: 'buyer', label: 'Buyer' },
  { key: 'seller', label: 'Seller' },
];

export const CATEGORY_LABELS = {
  commercial: 'Commercial',
  legal: 'Legal',
  tax: 'Tax',
  financing: 'Financing',
  hr: 'HR',
  other: 'Other',
};

export const DP_STATUS_LABELS = {
  open: 'Open',
  agreed: 'Agreed',
  fallenAway: 'Fallen away',
  escalated: 'Escalated',
};

// ---- Doc types -----------------------------------------------------------------
// dealDoc.docType is a key into the doc-type catalog (platform `docTypes/` +
// org `orgs/{org}/docTypes/`); dealDoc.docView is the renderer family. Every
// "is this a deck / workbook / design doc" branch reads docViewOf() so a deck
// filed as "Pitch deck" still renders as slides. Mirrors backend/shared/docTypes.js.
export const DOC_VIEWS = ['agreement', 'design', 'general', 'deck', 'workbook'];
export const DOC_FAMILIES = ['agreement', 'filing', 'corporate', 'financing', 'ip', 'employment', 'real-estate', 'litigation', 'regulatory', 'tax', 'deliverable', 'other'];
export const DOC_FAMILY_LABELS = {
  agreement: 'Agreements', filing: 'Filings', corporate: 'Corporate', financing: 'Financing', ip: 'IP', employment: 'Employment',
  'real-estate': 'Real estate', litigation: 'Litigation', regulatory: 'Regulatory', tax: 'Tax', deliverable: 'Work product', other: 'Other',
};
export function docViewOf(d) {
  if (!d) return 'agreement';
  if (DOC_VIEWS.includes(d.docView)) return d.docView;
  if (DOC_VIEWS.includes(d.docType)) return d.docType;
  const f = String(d.format || '').toLowerCase();
  if (f === 'pptx' || f === 'ppt' || f === 'key') return 'deck';
  if (f === 'xlsx' || f === 'xlsm' || f === 'xls' || f === 'csv') return 'workbook';
  return 'agreement';
}
export const slugDocType = (label) => String(label || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
// Human label for a doc's type given the loaded catalog (falls back to the key).
export function docTypeLabel(d, catalog) {
  const key = d?.docType || 'agreement';
  const e = (catalog || []).find((x) => x.key === key);
  return e ? e.label : key.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export const POSITION_STATUS_LABELS = {
  open: 'Open',
  agreed: 'Agreed',
  rejected: 'Rejected',
  countered: 'Countered',
};

// Decision view-model — the ONE vocabulary users filter and track by. Derived
// from the stored point status (open/agreed/fallenAway/escalated) plus the
// per-party position statuses, so a point that one side rejected or countered
// shows up as such without a schema change. Storage enums stay as they are.
export const DP_DECISIONS = ['open', 'accepted', 'rejected', 'countered', 'escalated', 'fallenAway'];
export const DP_DECISION_LABELS = {
  open: 'Open',
  accepted: 'Accepted',
  rejected: 'Rejected',
  countered: 'Countered',
  escalated: 'Escalated',
  fallenAway: 'Fallen away',
};
export const DP_DECISION_TONE = { open: 'b-info', accepted: 'b-ok', rejected: 'b-danger', countered: 'b-warn', escalated: 'b-danger', fallenAway: '' };

export function decisionOf(point) {
  if (!point) return 'open';
  if (point.status === 'agreed') return 'accepted';
  if (point.status === 'fallenAway') return 'fallenAway';
  if (point.status === 'escalated') return 'escalated';
  const statuses = Object.values(point.positions || {}).map((p) => p && p.status);
  if (statuses.includes('rejected')) return 'rejected';
  if (statuses.includes('countered')) return 'countered';
  return 'open';
}

// Audit types that record a decision on a point — the Decisions log reads
// the deal audit trail (one collection, already member-readable) filtered to
// these, newest first.
export const DP_DECISION_AUDIT_TYPES = new Set([
  'dealPoint.proposalAccepted', 'dealPoint.proposalRejected', 'dealPoint.status', 'dealPoint.position', 'dealPoint.accepted', 'dealPoint.undo',
]);

// "N accepted · M rejected this week" for the trackers, from the deal audit
// trail (already loaded on the dashboard). Status changes to agreed count as
// accepted so bulk register decisions show up alongside proposal responses.
export function decisionsSince(audit, sinceMs = Date.now() - 7 * 86400000) {
  const out = { accepted: 0, rejected: 0, total: 0 };
  for (const a of audit || []) {
    if (!DP_DECISION_AUDIT_TYPES.has(a.type) || (a.createdAt || 0) < sinceMs) continue;
    out.total += 1;
    if (a.type === 'dealPoint.proposalAccepted' || a.type === 'dealPoint.accepted' || (a.type === 'dealPoint.status' && /→ agreed$/.test(a.summary || ''))) out.accepted += 1;
    else if (a.type === 'dealPoint.proposalRejected' || (a.type === 'dealPoint.position' && /: rejected$/.test(a.summary || ''))) out.rejected += 1;
  }
  return out;
}

// Revert a decision the user just made (toast "Undo"): restores the point's
// status + positions snapshot as a logged edit, so the trail shows both the
// decision and its reversal rather than silently rewriting history.
export async function undoDecision(scope, actor, point, prev) {
  const patch = { status: prev.status || 'open', positions: prev.positions || {} };
  await updateEntity('dealPoint', scope, point.id, patch);
  await createEntity('dpEvent', { ...scope, dealPoint: point.id }, {
    type: 'edited', partyKey: actor.partyKey || '', summary: `Decision undone → ${patch.status}`, byUid: actor.byUid, byName: actor.byName,
  });
  await appendAudit(scope, actor, 'dealPoint.undo', `"${point.title}": decision undone → ${patch.status}`);
}

export const VISIBILITY_LABELS = {
  'with-xbp': 'With XBP',
  'drafted-unsent': 'In our draft — not yet circulated',
  'new-request': 'New request',
  internal: 'Internal only',
};

export const VISIBILITY_TONE = {
  'with-xbp': 'b-ok',
  'drafted-unsent': 'b-warn',
  'new-request': 'b-info',
  internal: '',
};

// Human labels for version-manifest sources — shared by the document view's
// changes modal and the audit trail's expandable change detail.
// File cabinets — where a document sits in the project's flow of paper:
// 'input' (what came in), 'deliverable' (what goes out), '' = working paper.
// `dealDoc.cabinet` is the field; legacy #input / #deliverable tags are
// honored for docs filed before the field existed.
export const cabinetOf = (d) => {
  if (d?.cabinet === 'input' || d?.cabinet === 'deliverable') return d.cabinet;
  // An explicit field (even '' = working paper) wins over legacy tags, so a
  // tagged doc can be un-filed through the Cabinet… mover.
  if (d?.cabinet != null) return '';
  const tags = d?.tags || [];
  if (tags.includes('deliverable')) return 'deliverable';
  if (tags.includes('input')) return 'input';
  return '';
};

// Curated document-tag taxonomy — suggestions, not an enum (free-form tags
// are welcome). Tags feed the project brain: provenance tags tell retrieval
// what the ORIGINAL was vs what was DERIVED from it, instrument tags give
// structure to a growing document set, evidence tags mark filed paper.
export const DOC_TAG_GROUPS = [
  {
    label: 'Provenance',
    tags: ['original', 'source-template', 'derived', 'received', 'internal-draft', 'executed', 'superseded'],
  },
  {
    label: 'Instrument',
    tags: ['operating-agreement', 'articles', 'certificate', 'amendment', 'resolution', 'consent', 'bylaws', 'term-sheet', 'side-letter', 'nda', 'msa'],
  },
  {
    label: 'Filings & evidence',
    tags: ['state-filing', 'good-standing', 'ein', 'foreign-qualification', 'signature-page'],
  },
  {
    label: 'Context',
    tags: ['correspondence', 'diligence', 'reference', 'playbook'],
  },
];

export const MANIFEST_SOURCE_LABEL = {
  agent: 'drafting agent', manual: 'manual edit', dealpoint: 'deal point',
  create: 'initial draft', revert: 'revert', convert: 'converted from document',
  upload: 'received draft', derive: 'derived from source agreement',
};

// Actor identity attached to audit-trailed writes.
export function actorOf(user, participant) {
  return {
    byUid: user?.uid || '',
    byName: user?.displayName || user?.email || 'Unknown',
    partyKey: participant?.partyKey || '',
  };
}

export async function appendAudit(scope, actor, type, summary, meta = {}) {
  await db.collection(`orgs/${scope.org}/deals/${scope.deal}/audit`).add({
    type,
    summary: String(summary).slice(0, 2000),
    meta,
    ...actor,
    createdAt: Date.now(),
  });
}

// ---- Deal points -------------------------------------------------------------------
export async function createDealPoint(scope, actor, fields) {
  const id = await createEntity('dealPoint', scope, {
    ...fields,
    createdBy: actor.byUid,
  });
  await db.collection(`orgs/${scope.org}/deals/${scope.deal}/dealPoints/${id}/events`).add({
    type: 'created',
    partyKey: actor.partyKey,
    summary: `Created: ${fields.title}`,
    byUid: actor.byUid,
    byName: actor.byName,
    createdAt: Date.now(),
  });
  await appendAudit(scope, actor, 'dealPoint.created', `Deal point created: "${fields.title}"`);
  return id;
}

// Record a party's position on a deal point (positions map keyed by partyKey).
export async function setPosition(scope, actor, dealPoint, partyKey, position) {
  const positions = { ...(dealPoint.positions || {}) };
  positions[partyKey] = {
    stance: position.stance || '',
    rationale: position.rationale || '',
    status: position.status || 'open',
    byName: actor.byName,
    at: Date.now(),
  };
  await updateEntity('dealPoint', scope, dealPoint.id, { positions });
  await db.collection(`orgs/${scope.org}/deals/${scope.deal}/dealPoints/${dealPoint.id}/events`).add({
    type: 'position',
    partyKey,
    summary: `${partyKey} → ${position.status || 'open'}: ${position.stance || ''}`.slice(0, 2000),
    byUid: actor.byUid,
    byName: actor.byName,
    createdAt: Date.now(),
  });
  await appendAudit(scope, actor, 'dealPoint.position', `Position on "${dealPoint.title}" (${partyKey}): ${position.status}`);
}

export async function setDealPointStatus(scope, actor, dealPoint, status) {
  await updateEntity('dealPoint', scope, dealPoint.id, { status });
  await db.collection(`orgs/${scope.org}/deals/${scope.deal}/dealPoints/${dealPoint.id}/events`).add({
    type: 'status',
    partyKey: actor.partyKey,
    summary: `Status → ${status}`,
    byUid: actor.byUid,
    byName: actor.byName,
    createdAt: Date.now(),
  });
  await appendAudit(scope, actor, 'dealPoint.status', `"${dealPoint.title}" → ${status}`);
}

export async function setDealPointLock(scope, actor, dealPoint, locked) {
  await updateEntity('dealPoint', scope, dealPoint.id, { locked, lockedBy: locked ? actor.byUid : '' });
  await db.collection(`orgs/${scope.org}/deals/${scope.deal}/dealPoints/${dealPoint.id}/events`).add({
    type: locked ? 'locked' : 'unlocked',
    partyKey: actor.partyKey,
    summary: locked ? 'Point locked (linked language frozen)' : 'Point unlocked',
    byUid: actor.byUid,
    byName: actor.byName,
    createdAt: Date.now(),
  });
  await appendAudit(scope, actor, locked ? 'dealPoint.locked' : 'dealPoint.unlocked', `"${dealPoint.title}" ${locked ? 'locked' : 'unlocked'}`);
}

// Accept an AI-suggested point into the live register (or dismiss = delete).
export async function acceptSuggestedPoint(scope, actor, dealPoint) {
  await updateEntity('dealPoint', scope, dealPoint.id, { suggested: false });
  await appendAudit(scope, actor, 'dealPoint.accepted', `AI-suggested point accepted: "${dealPoint.title}"`);
}

// Accept or reject another party's proposal on a deal point. Accepting adopts
// their language as your party's position (status agreed); if that leaves
// every party agreed, the point itself flips to agreed automatically.
export async function respondToProposal(scope, actor, dealPoint, parties, fromPartyKey, myPartyKey, accept) {
  const from = (dealPoint.positions || {})[fromPartyKey] || {};
  const fromLabel = parties.find((p) => p.key === fromPartyKey)?.label || fromPartyKey;
  const positions = { ...(dealPoint.positions || {}) };
  positions[myPartyKey] = accept
    ? {
        stance: from.stance || '',
        rationale: `Accepted ${fromLabel}'s proposal`,
        status: 'agreed',
        byName: actor.byName,
        at: Date.now(),
      }
    : {
        stance: positions[myPartyKey]?.stance || '',
        rationale: `Rejected ${fromLabel}'s proposal`,
        status: 'rejected',
        byName: actor.byName,
        at: Date.now(),
      };
  await updateEntity('dealPoint', scope, dealPoint.id, { positions });
  await db.collection(`orgs/${scope.org}/deals/${scope.deal}/dealPoints/${dealPoint.id}/events`).add({
    type: accept ? 'accepted' : 'rejected',
    partyKey: myPartyKey,
    summary: `${myPartyKey} ${accept ? 'ACCEPTED' : 'REJECTED'} ${fromPartyKey}'s proposal${from.stance ? `: ${from.stance}` : ''}`.slice(0, 2000),
    byUid: actor.byUid,
    byName: actor.byName,
    createdAt: Date.now(),
  });
  await appendAudit(
    scope, actor,
    accept ? 'dealPoint.proposalAccepted' : 'dealPoint.proposalRejected',
    `"${dealPoint.title}": ${myPartyKey} ${accept ? 'accepted' : 'rejected'} ${fromPartyKey}'s proposal`
  );
  // Full alignment → the point is agreed.
  if (accept && parties.length && parties.every((p) => positions[p.key]?.status === 'agreed')) {
    await setDealPointStatus(scope, actor, dealPoint, 'agreed');
  }
}

// Push agreed language into the official draft: replaces the linked clause's
// text in the doc's current version and publishes it as a new version.
export async function applyLanguageToDraft(scope, actor, clauseRef, newText, dealPoint, dealPoints) {
  const docSnap = await db.doc(`orgs/${scope.org}/deals/${scope.deal}/docs/${clauseRef.docId}`).get();
  if (!docSnap.exists) throw new Error('Linked document not found');
  const dealDoc = { id: docSnap.id, ...docSnap.data() };
  const vSnap = await db
    .doc(`orgs/${scope.org}/deals/${scope.deal}/docs/${dealDoc.id}/versions/${dealDoc.currentVersionId}`)
    .get();
  if (!vSnap.exists) throw new Error('Current version not found');
  const version = vSnap.data();
  version.clauses = await loadVersionClauses(version);
  if (!version.clauses.some((c) => c.id === clauseRef.clauseId)) {
    throw new Error('Linked clause no longer exists in the current draft');
  }
  const prev = version.clauses.find((c) => c.id === clauseRef.clauseId);
  const clauses = version.clauses.map((c) => (c.id === clauseRef.clauseId ? { ...c, text: newText } : c));
  return publishVersion(scope, actor, dealDoc, {
    clauses,
    label: `Agreed: ${dealPoint.title}`,
    note: `Agreed language applied from deal point "${dealPoint.title}"`,
    baseVersionId: dealDoc.currentVersionId,
    dealPoints,
    manifest: {
      source: 'dealpoint',
      instruction: `Agreed language from deal point "${dealPoint.title}"`,
      ops: [{ op: 'edit', id: clauseRef.clauseId, before: { heading: prev.heading, text: prev.text }, after: { heading: prev.heading, text: newText } }],
    },
  });
}

// ---- Versions ------------------------------------------------------------------------
// Publish a new immutable version of a document (edited clauses + snapshot of
// the current Deal Points Register), and point the doc at it.
export async function publishVersion(scope, actor, dealDoc, { clauses, label, note, baseVersionId, dealPoints, manifest }) {
  const snapshot = (dealPoints || [])
    .filter((p) => !p.suggested)
    .map((p) => ({
      id: p.id,
      title: p.title,
      status: p.status,
      locked: !!p.locked,
      positions: Object.fromEntries(
        Object.entries(p.positions || {}).map(([k, v]) => [k, { status: v.status || 'open' }])
      ),
    }));

  // Firestore caps documents at 1 MiB — big clause payloads publish through
  // the backend, which spills the array to S3 and writes a pointer version.
  if (new Blob([JSON.stringify(clauses)]).size > 600_000) {
    const { versionId } = await serverPublishVersion({
      orgId: scope.org,
      dealId: scope.deal,
      docId: dealDoc.id,
      clauses,
      label: label || '',
      note: note || '',
      // The server pins the commit to this base version and rejects (409)
      // if the draft moved while the change was being prepared.
      baseVersionId: baseVersionId || dealDoc.currentVersionId || '',
      partyKey: actor.partyKey || '',
      dealPointsSnapshot: snapshot,
      manifest: manifest || null,
    });
    return versionId;
  }

  // Version doc + doc pointer commit in ONE transaction, pinned to the
  // version this change was computed against. Concurrent AI tasks publish on
  // the same docs (edit, ingest, answer-apply); without the pin, two writers
  // mint the same version number and the loser's changes silently drop out
  // of the draft. On conflict the caller gets a clear "draft moved" error.
  const docRef = db.doc(`orgs/${scope.org}/deals/${scope.deal}/docs/${dealDoc.id}`);
  const vRef = docRef.collection('versions').doc();
  const base = baseVersionId || dealDoc.currentVersionId || '';
  const published = await db.runTransaction(async (tx) => {
    const snap = await tx.get(docRef);
    const cur = snap.data() || {};
    if (base && cur.currentVersionId && cur.currentVersionId !== base) {
      const err = new Error(
        `The draft moved to v${cur.versionCount} while this change was being prepared — reload and re-apply it against the latest version.`
      );
      err.code = 'version-conflict';
      throw err;
    }
    const n = (cur.versionCount || 0) + 1;
    tx.set(vRef, {
      number: n,
      label: label || `Version ${n}`,
      note: note || '',
      authorUid: actor.byUid,
      authorName: actor.byName,
      partyKey: actor.partyKey,
      clauses,
      dealPointsSnapshot: snapshot,
      baseVersionId: base,
      // Changes manifest: what produced this version (traceable, revertible).
      ...(manifest ? { manifest } : {}),
      sealed: false, // the backend checkpoint worker seals + archives it
      createdAt: Date.now(),
    });
    tx.set(docRef, { currentVersionId: vRef.id, versionCount: n, updatedAt: Date.now() }, { merge: true });
    return n;
  });
  await appendAudit(scope, actor, 'version.published', `v${published} of "${dealDoc.title}" published${actor.partyKey ? ` on behalf of ${actor.partyKey}` : ''}${label ? ` — ${label}` : ''}`, { docId: dealDoc.id, versionId: vRef.id });
  return vRef.id;
}

// ---- Comments ---------------------------------------------------------------------
export async function addComment(scope, actor, anchor, body, mentions = []) {
  await createEntity('comment', scope, {
    body,
    anchor,
    mentions,
    authorUid: actor.byUid,
    authorName: actor.byName,
    partyKey: actor.partyKey,
  });
}
